import { useRef, useState, useCallback, useEffect } from "react";

export interface ExtractionTimerState {
  /** The displayed estimated time remaining string, e.g. "Est. ~3 min remaining" */
  estimatedTimeLabel: string | null;
  /** Reset the timer state and clear the label (call when extraction is cancelled, errors, or completes) */
  reset: () => void;
  /**
   * Reset the timer state and immediately show a formula-based baseline estimate
   * (totalItems * DEFAULT_SECONDS_PER_DOC), so there's a useful number on screen
   * before any real progress has been polled. Call when extraction is triggered.
   */
  start: (totalItems: number) => void;
  /** Update the timer with the latest progress (call on each poll) */
  update: (current: number, total: number) => void;
}

interface ProgressSnapshot {
  current: number;
  timestamp: number;
}

/**
 * Formats seconds into a human-readable rounded time string.
 * Uses whole numbers only (no decimals) as per acceptance criteria.
 */
function formatTimeRemaining(seconds: number): string {
  if (seconds < 60) {
    // Round to nearest 5 seconds for very short times
    const rounded = Math.max(5, Math.round(seconds / 5) * 5);
    return `${rounded} sec`;
  }
  if (seconds < 3600) {
    const minutes = Math.round(seconds / 60);
    return `${minutes} min`;
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (minutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${minutes} min`;
}

/**
 * Custom hook that estimates extraction time remaining based on
 * processing rate observed over time.
 *
 * Key behaviours (per acceptance criteria):
 * - Only updates the displayed estimate if the new value deviates by >25%
 *   from the previously displayed value.
 * - Uses rounded numbers (no decimals).
 * - Refreshes periodically as extraction speeds up or slows down.
 * - For PICO projects the total count is only known after the backend
 *   processes population categories; the hook accepts dynamic total updates.
 * - On start(), shows an immediate baseline estimate (totalItems * DEFAULT_SECONDS_PER_DOC)
 *   instead of a generic placeholder; real progress then adjusts up/down from that baseline.
 */

/** Flat fallback estimate used to seed the baseline shown immediately on start(). */
const DEFAULT_SECONDS_PER_DOC = 30;

export function useExtractionTimer(): ExtractionTimerState {
  const [estimatedTimeLabel, setEstimatedTimeLabel] = useState<string | null>(null);

  // Track progress snapshots to compute rate
  const snapshotsRef = useRef<ProgressSnapshot[]>([]);
  // The last displayed estimate in seconds (for 25% deviation check)
  const lastDisplayedSecondsRef = useRef<number | null>(null);
  // Track whether extraction is active
  const activeRef = useRef(false);

  const reset = useCallback(() => {
    snapshotsRef.current = [];
    lastDisplayedSecondsRef.current = null;
    activeRef.current = false;
    setEstimatedTimeLabel(null);
  }, []);

  const start = useCallback((totalItems: number) => {
    snapshotsRef.current = [];
    activeRef.current = false;

    if (totalItems > 0) {
      const baselineSeconds = totalItems * DEFAULT_SECONDS_PER_DOC;
      lastDisplayedSecondsRef.current = baselineSeconds;
      setEstimatedTimeLabel(`Est. ~${formatTimeRemaining(baselineSeconds)} remaining`);
    } else {
      lastDisplayedSecondsRef.current = null;
      setEstimatedTimeLabel("Est. ~calculating...");
    }
  }, []);

  const update = useCallback((current: number, total: number) => {
    if (total <= 0 || current < 0) {
      return;
    }

    // If extraction is complete, clear the label
    if (current >= total) {
      snapshotsRef.current = [];
      lastDisplayedSecondsRef.current = null;
      activeRef.current = false;
      setEstimatedTimeLabel(null);
      return;
    }

    const now = Date.now();

    // Mark as active on first update
    if (!activeRef.current) {
      activeRef.current = true;
      snapshotsRef.current = [{ current, timestamp: now }];
      // Only fall back to the generic placeholder if start() didn't already seed a baseline.
      if (lastDisplayedSecondsRef.current === null) {
        setEstimatedTimeLabel("Est. ~calculating...");
      }
      return; // Need at least 2 snapshots to compute rate
    }

    // Add snapshot
    snapshotsRef.current.push({ current, timestamp: now });

    // Keep only the last 6 snapshots (~60s of history at 10s polling) to smooth rate
    if (snapshotsRef.current.length > 6) {
      snapshotsRef.current = snapshotsRef.current.slice(-6);
    }

    const snapshots = snapshotsRef.current;
    if (snapshots.length < 2) return;

    // Compute rate using oldest and newest snapshot in window
    const oldest = snapshots[0];
    const newest = snapshots[snapshots.length - 1];
    const elapsedMs = newest.timestamp - oldest.timestamp;
    const processed = newest.current - oldest.current;

    // If no progress yet (0 docs processed in window), show a waiting message
    if (processed <= 0 || elapsedMs <= 0) {
      if (lastDisplayedSecondsRef.current === null) {
        setEstimatedTimeLabel("Est. ~calculating...");
      }
      return;
    }

    // Rate: documents per millisecond
    const ratePerMs = processed / elapsedMs;
    const remaining = total - current;
    const estimatedRemainingMs = remaining / ratePerMs;
    const estimatedRemainingSeconds = estimatedRemainingMs / 1000;

    // Apply 25% deviation rule: only update if the new estimate deviates
    // from the last displayed value by more than 25%
    const lastDisplayed = lastDisplayedSecondsRef.current;
    if (lastDisplayed !== null) {
      const deviation = Math.abs(estimatedRemainingSeconds - lastDisplayed) / lastDisplayed;
      if (deviation <= 0.25) {
        // Not enough change - keep the current displayed estimate
        return;
      }
    }

    // Update displayed estimate
    lastDisplayedSecondsRef.current = estimatedRemainingSeconds;
    const formatted = formatTimeRemaining(estimatedRemainingSeconds);
    setEstimatedTimeLabel(`Est. ~${formatted} remaining`);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      activeRef.current = false;
    };
  }, []);

  return { estimatedTimeLabel, reset, start, update };
}
